import type { ComponentProps } from "react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { CheckCircle2, XCircle, AlertCircle, Info } from "lucide-react";

type ToasterProps = ComponentProps<typeof Sonner>;

type ToastError = (message: unknown, options?: Record<string, unknown>) => unknown;

const toast: Omit<typeof sonnerToast, "error"> & { error: ToastError } = {
  ...sonnerToast,
  // Errors should never auto-dismiss so users don't miss critical problems.
  error: (message, options) => sonnerToast.error(message as any, { ...(options as any), duration: Infinity }),
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="top-center"
      className="toaster group"
      duration={4000}
      closeButton
      gap={8}
      icons={{
        success: <CheckCircle2 className="h-5 w-5" />,
        error: <XCircle className="h-5 w-5" />,
        warning: <AlertCircle className="h-5 w-5" />,
        info: <Info className="h-5 w-5" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-card group-[.toaster]:text-card-foreground group-[.toaster]:border-border/40 group-[.toaster]:shadow-card group-[.toaster]:rounded-lg group-[.toaster]:min-w-[320px] group-[.toaster]:max-w-[440px] group-[.toaster]:py-3 group-[.toaster]:px-4 group-[.toaster]:pr-10 relative",
          title: "group-[.toast]:text-sm group-[.toast]:font-medium group-[.toast]:text-card-foreground",
          description: "group-[.toast]:text-muted-foreground group-[.toast]:text-xs",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:text-xs group-[.toast]:font-medium",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:text-xs",
          closeButton:
            "!absolute !top-2 !right-2 !left-auto !bg-white !text-gray-900 !border-border/40 hover:!bg-gray-100 !shadow-sm [&>svg]:!stroke-gray-900",
          error:
            "group-[.toaster]:bg-card group-[.toaster]:text-destructive group-[.toaster]:border-destructive/30",
          success:
            "group-[.toaster]:bg-card group-[.toaster]:text-primary group-[.toaster]:border-primary/30",
          warning:
            "group-[.toaster]:bg-card group-[.toaster]:text-accent group-[.toaster]:border-accent/30",
          info:
            "group-[.toaster]:bg-card group-[.toaster]:text-card-foreground group-[.toaster]:border-border/40",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };

